import React, { useEffect, useState } from "react";
import { getProductByCategoryUrl, getSubCategoriesByCategoryId } from "../../../utils/urls";
import axios from "axios";
import Skeleton from "../../../components/Skeleton";
import InfiniteScrollWrapper from "../../../components/common/InfiniteScrollWrapper";

function SelectProduct({ onNext, onBack, onUpdate, selectedCategory }) {
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedCategory?.is_lead_add_without_product) {
      fetchSubCategories();
    }
  }, [selectedCategory]);

  const fetchSubCategories = async () => {
    try {
      const response = await axios.get(
        getSubCategoriesByCategoryId(selectedCategory?.category_id) + "?is_all=true"
      );
      if (response.data.success) {
        const data = response.data.data.filter(sub => sub.is_active && !sub.is_delete);
        setSubCategories(data);
      }
    } catch (error) {
      console.error("Error fetching subCategories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNext = () => {
    if (selectedCategory?.is_lead_add_without_product) {
      if (selectedSubCategory) {
        onUpdate(selectedSubCategory, "sub_category");
        onNext();
      }
    } else {
      if (selectedProduct) {
        onUpdate(selectedProduct, "product");
        onNext();
      }
    }
  };

  const ProductCard = ({ data }) => {
    const isSelected = selectedProduct?.product_id === data?.product_id;

    return (
      <div
        className={`border rounded-lg p-4 shadow-sm bg-white cursor-pointer transition-all duration-200 ${
          isSelected ? "border-blue-600 ring-2 ring-blue-100" : "border-gray-200"
        }`}
        onClick={() => setSelectedProduct(data)}
      >
        <div className="flex gap-3 items-start">
          <img src={data?.company_logo} alt="company-logo" className="w-12 h-12 object-contain rounded-md" />
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-gray-800 leading-snug">{data?.company_name}</p>
            <p className="text-sm font-medium text-gray-600 leading-tight">{data?.product_name}</p>
          </div>
        </div>

        <div className="mt-3">
          <p className="text-sm font-semibold text-gray-700 mb-1">Description:</p>
          <p className="text-sm text-gray-600 line-clamp-3">{data?.description}</p>
        </div>

        <div className="mt-2">
          <p className="text-sm font-semibold text-gray-700 mb-1">Financial Description:</p>
          <p className="text-sm text-gray-600">{data?.financial_description}</p>
        </div>

        {/* <div className="mt-3 text-sm font-medium text-blue-600 hover:underline">
          View Details
        </div> */}
      </div>
    );
  };

  return (
    <div className="pt-4 border-t border-[#E2EBF3]">
      <p className="text-[1rem] font-semibold text-[#2C2C2C] mb-4">
        {selectedCategory?.is_lead_add_without_product ? "Select a Subcategory" : "Select a Product"}
      </p>

      {selectedCategory?.is_lead_add_without_product ? (
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 h-[calc(100vh_-_24rem)] overflow-y-auto">
          {(loading ? Array(6).fill(null) : subCategories).map((sub, index) => {
            const isSelected = sub?.sub_category_id === selectedSubCategory?.sub_category_id;
            return (
              <div
                key={sub?.sub_category_id || index}
                onClick={() => sub && setSelectedSubCategory(sub)}
                className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border 
                  ${isSelected ? "border-blue-600 ring-2 ring-blue-100" : "border-gray-100"} 
                  flex flex-col cursor-pointer`}
              >
                <div className="flex items-center gap-4 mb-4">
                  {sub?.logo_url ? (
                    <img
                      src={sub.logo_url}
                      alt={sub.title}
                      className="w-14 h-14 object-contain rounded-full border border-gray-200"
                    />
                  ) : (
                    <Skeleton className="w-14 h-14 rounded-full" />
                  )}
                  {sub?.title ? (
                    <h2 className="text-lg font-semibold text-gray-800">{sub.title}</h2>
                  ) : (
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32 rounded" />
                      <Skeleton className="h-3 w-20 rounded" />
                    </div>
                  )}
                </div>

                {sub?.description ? (
                  <p className="text-sm text-gray-600 overflow-hidden text-ellipsis" style={{ maxHeight: "4.5em" }}>
                    {sub.description}
                  </p>
                ) : (
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-full rounded" />
                    <Skeleton className="h-3 w-5/6 rounded" />
                    <Skeleton className="h-3 w-4/5 rounded" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <InfiniteScrollWrapper
          url={getProductByCategoryUrl}
          params={{ category_id: selectedCategory?.category_id }}
          pageLimit={10}
          height="calc(100vh - 24rem)"
          scrollableDivClassName="max-h-[calc(100vh_-_24rem)]"
          ItemComp={ProductCard}
          gap={"1.5rem"}
          size={{ xxs: 12, xs: 12, md: 6, lg: 6 }}
        />
      )}

      <div className="flex justify-between gap-4 mt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSaveNext}
          disabled={
            selectedCategory?.is_lead_add_without_product
              ? !selectedSubCategory
              : !selectedProduct
          }
          className={`px-4 py-2 rounded-md text-white ${
            (selectedCategory?.is_lead_add_without_product
              ? selectedSubCategory
              : selectedProduct)
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Save & Next
        </button>
      </div>
    </div>
  );
}

export default SelectProduct;
